// The view lives in the URL, so a link or a reload brings back the same scene: scene, view,
// selection, isolation, section, time and presentation. Written with replaceState (no history
// spam); read once at start.

import { useAtlas, type AtlasState } from './store.ts';
import { SCENARIOS, scenarioById, type ScenarioId } from '../scenarios/index.ts';
import type { SectionPlane, Target } from '../viewport/api.ts';

const AXES = new Set(['axial', 'coronal', 'sagittal']);

function encodeTarget(t: Target | null): string | null {
  if (!t) return null;
  if ('id' in t) return t.id;
  if ('system' in t) return `system:${t.system}`;
  return null;
}

function decodeTarget(v: string | null): Target | null {
  if (!v) return null;
  if (v.startsWith('system:')) return { system: v.slice(7) };
  return { id: v };
}

export function toQuery(s: Pick<AtlasState, 'scenario' | 'preset' | 'selected' | 'isolate' | 'section' | 'time' | 'presenting'>): string {
  const q = new URLSearchParams();
  if (s.scenario !== 'explore') q.set('scene', s.scenario);
  if (s.preset && s.preset !== scenarioById(s.scenario).presets[0]?.id) q.set('view', s.preset);
  if (s.selected) q.set('select', s.selected);
  const iso = encodeTarget(s.isolate);
  if (iso && !s.preset) q.set('isolate', iso);
  const plane = s.section[0];
  if (plane && !s.preset) q.set('section', `${plane.axis}:${Math.round(plane.mm)}`);
  if (s.time > 0) q.set('t', s.time.toFixed(3).replace(/\.?0+$/, ''));
  if (s.presenting) q.set('present', '1');
  return q.toString();
}

/** Apply a query to the store. Unknown values are ignored, so an old link still opens. */
export function fromQuery(search: string) {
  const q = new URLSearchParams(search);
  const s = useAtlas.getState();
  const scene = q.get('scene');
  if (scene && SCENARIOS.some((x) => x.id === scene)) s.setScenario(scene as ScenarioId);
  const sc = scenarioById(useAtlas.getState().scenario);
  const view = sc.presets.find((p) => p.id === q.get('view'));
  if (view) s.applyPreset(view);
  const iso = decodeTarget(q.get('isolate'));
  if (iso) s.isolateTarget(iso);
  const sec = q.get('section')?.split(':');
  if (sec && AXES.has(sec[0]!) && Number.isFinite(Number(sec[1]))) s.setSection([{ axis: sec[0] as SectionPlane['axis'], mm: Number(sec[1]), keep: 'negative' }]);
  const sel = q.get('select');
  if (sel) s.select(sel);
  const t = Number(q.get('t'));
  if (Number.isFinite(t) && t > 0) s.setTime(t);
  if (q.get('present') === '1') s.setPresenting(true);
}

export function syncUrl() {
  let last = '';
  let timer = 0;
  const write = () => {
    const s = useAtlas.getState();
    const keep = new URLSearchParams(location.search);
    const next = new URLSearchParams(toQuery(s));
    // Developer switches (engine=mock, stats) survive.
    for (const k of ['engine', 'stats']) {
      const v = keep.get(k);
      if (v !== null) next.set(k, v);
    }
    const str = next.toString();
    if (str === last) return;
    last = str;
    history.replaceState(null, '', str ? `?${str}` : location.pathname);
  };
  useAtlas.subscribe(
    (s) => toQuery(s),
    () => {
      // Time changes every frame while playing: write at most a few times a second.
      clearTimeout(timer);
      timer = window.setTimeout(write, 250);
    },
  );
}
